/**
 * Draft safety for the lesson editor. Pure, so it can be tested without
 * Firebase or React (draftGuard.test.ts).
 *
 * Two rules:
 *   1. Nothing is written until the host has finished reading the stored
 *      draft (decideAutosave, savesAllowed). An editor that opened before its
 *      draft arrived holds an empty plan, and the first write from it would
 *      replace the coach's segments and recordings.
 *   2. An empty plan is never written over a non-empty stored draft
 *      (refuseEmptyOverwrite), unless this editor session itself loaded that
 *      non-empty plan, which means the coach emptied it on purpose.
 */

export interface PlanLike {
  notableSegments?: readonly unknown[];
  voiceoverNotableSegmentClips?: readonly unknown[];
  notes?: string;
  coachNotes?: string;
  attachedDrills?: readonly unknown[];
}

/** True when the plan carries nothing a coach made. */
export function isEmptyPlan(plan: PlanLike | null | undefined): boolean {
  if (!plan) return true;
  return (plan.notableSegments?.length ?? 0) === 0 &&
    (plan.voiceoverNotableSegmentClips?.length ?? 0) === 0 &&
    (plan.attachedDrills?.length ?? 0) === 0 &&
    !(plan.notes ?? '').trim() &&
    !(plan.coachNotes ?? '').trim();
}

/**
 * Whether a save must be refused: an empty incoming plan over a stored plan
 * that is not empty. `allowEmptyOverwrite` is set only by an editor that
 * loaded a non-empty plan in this session.
 */
export function refuseEmptyOverwrite(
  incoming: PlanLike,
  stored: PlanLike | null | undefined,
  allowEmptyOverwrite = false,
): boolean {
  return !allowEmptyOverwrite && isEmptyPlan(incoming) && !isEmptyPlan(stored);
}

/** Any write at all (auto-save, close, page hide) waits for the draft read. */
export function savesAllowed(draftReady: boolean, hasSaver: boolean): boolean {
  return draftReady && hasSaver;
}

export type AutosaveAction =
  /** Draft not read yet, a clip is still in the recorder, or no saver: do nothing. */
  | 'wait'
  /** First pass after load: take what is on screen as the baseline, write nothing. */
  | 'adopt'
  /** On screen matches what was last written. */
  | 'clean'
  /** Something changed: arm the debounced write. */
  | 'save';

/** One pass of the editor's auto-save effect. */
export function decideAutosave(input: {
  draftReady: boolean;
  hasSaver: boolean;
  clipEditorOpen: boolean;
  lastSavedKey: string | null;
  key: string;
}): AutosaveAction {
  if (!savesAllowed(input.draftReady, input.hasSaver) || input.clipEditorOpen) return 'wait';
  if (input.lastSavedKey === null) return 'adopt';
  if (input.key === input.lastSavedKey) return 'clean';
  return 'save';
}
